import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAppData } from '@/app-data';
import { useRealtime } from '@/features/realtime/realtime';
import * as api from '@/features/backlog/api';
import { Status } from '@/features/backlog/types';

/** The presentational bits of a status, resolved for a given value (with a fallback). */
export interface StatusMeta {
  value: string;
  label: string;
  color: string;
}

const UNKNOWN_COLOR = '#a1a1aa';

/** Meta for a value with no matching status (e.g. an item briefly out of sync after a
 *  column was deleted elsewhere) — a neutral swatch labelled by the raw value. */
export const fallbackStatusMeta = (value: string): StatusMeta => ({
  value,
  label: value || 'Unknown',
  color: UNKNOWN_COLOR,
});

interface StatusesCtx {
  /** The current board's statuses, in column order. */
  statuses: Status[];
  byValue: Map<string, Status>;
  /** Label + colour for a status value, falling back gracefully for unknown values. */
  metaOf: (value: string) => StatusMeta;
  /** The value a new item defaults to (the first column, or 'not_started' before load). */
  defaultValue: string;
  /**
   * A stable key over the SET of status values (order-independent). It changes only when a
   * status is added or removed — not on rename/recolour/reorder — so item views can use it
   * as a reload trigger: a delete reassigns items server-side, and this is how a view knows
   * to refetch them. (Board switches carry their own boardId reload; this is additive.)
   */
  valuesKey: string;
  loading: boolean;
  reload: () => Promise<void>;
}

const Ctx = createContext<StatusesCtx | null>(null);

export const useStatuses = (): StatusesCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStatuses must be used within StatusesProvider');
  return v;
};

/** Convenience: just the value→meta resolver (label/colour). */
export const useStatusMeta = (): ((value: string) => StatusMeta) => useStatuses().metaOf;

/**
 * Loads and tracks the CURRENT board's statuses, and keeps them live: a status.sync
 * broadcast (any create/rename/recolour/reorder/delete, from anyone) carries the board's
 * full list, which simply replaces ours. Must sit inside AppDataProvider (current board)
 * and RealtimeProvider (the event stream).
 */
export const StatusesProvider = ({ children }: { children: ReactNode }) => {
  const { currentBoardId } = useAppData();
  const { subscribeItems } = useRealtime();
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(false);
  // Monotonic token so an out-of-order / stale response (a slow load for a board we've since left)
  // can never overwrite the current board's statuses. Bumped on every load and on clear.
  const reqRef = useRef(0);

  const load = useCallback(async (boardId: string) => {
    const my = ++reqRef.current;
    setLoading(true);
    try {
      const next = await api.listStatuses(boardId);
      if (reqRef.current === my) setStatuses(next);
    } finally {
      if (reqRef.current === my) setLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    if (currentBoardId) await load(currentBoardId);
  }, [currentBoardId, load]);

  // (Re)load on board change. Clear FIRST — always, not only when no board is selected — so a
  // switch never renders the previous board's columns (which would let a create send a status
  // that doesn't exist on the new board); the fetch then fills in the new board's set.
  useEffect(() => {
    reqRef.current++; // invalidate any in-flight load for the board we're leaving
    setStatuses([]);
    if (currentBoardId) load(currentBoardId);
  }, [currentBoardId, load]);

  // Live status changes for the active board. The payload is the board's whole list, so we
  // replace wholesale; own-tab echoes are applied too (idempotent — same data we set locally).
  useEffect(() => {
    if (!currentBoardId) return undefined;
    return subscribeItems((ev) => {
      if (ev.type !== 'status.sync' || ev.board_id !== currentBoardId) return;
      const next = (ev.payload as { statuses?: Status[] } | undefined)?.statuses;
      if (Array.isArray(next)) setStatuses(next);
    });
  }, [currentBoardId, subscribeItems]);

  const byValue = useMemo(() => new Map(statuses.map((s) => [s.value, s])), [statuses]);
  const metaOf = useCallback(
    (value: string): StatusMeta => {
      const s = byValue.get(value);
      return s ? { value: s.value, label: s.label, color: s.color } : fallbackStatusMeta(value);
    },
    [byValue],
  );
  const defaultValue = statuses[0]?.value ?? 'not_started';
  const valuesKey = useMemo(() => statuses.map((s) => s.value).slice().sort().join('|'), [statuses]);

  const value = useMemo<StatusesCtx>(
    () => ({ statuses, byValue, metaOf, defaultValue, valuesKey, loading, reload }),
    [statuses, byValue, metaOf, defaultValue, valuesKey, loading, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
